import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Geolocation } from '@capacitor/geolocation';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { environment } from '../../../environments/environment';
import { AuthToken, AuthenticationResponse, LogoutResponse, PsRegistrationData } from '../models/auth-token.model';
import { authTokenToModel, decodeUserFromJwt, forgetPasswordToModel, registerToPayload, registerUserToModel } from '../api/adapters';
import { storagePrefix } from '../utils/storage-key';
import { SentryService } from './sentry.service';
import { IdbStorageService, IDB_STORES_CLEARED_ON_LOGOUT } from './idb-storage.service';

/**
 * Unscoped bootstrap key — the only auth key stored without a server entity ID.
 * Read first on startup to decode the JWT and derive the entityId, which then
 * constructs all other scoped storage key names.
 */
const ACCESS_TOKEN_KEY = 'auth_access_token';

/** Base path for all auth endpoints. */
const AUTH_BASE = `${environment.apiUrl}/auth`;

/**
 * JS-level geolocation safety timeout (ms). Slightly longer than the native
 * `timeout` hint passed to `getCurrentPosition` so that if the iOS
 * `CLLocationManager` RunLoop hang causes the native timeout to silently fail,
 * `Promise.race` still rejects and we fall back to `"0"/"0"`.
 */
const GEO_JS_TIMEOUT_MS = 6000;

/**
 * Returns the three scoped auth key names for the given process server entity ID.
 * Keys are prefixed with an opaque FNV-1a hash so the raw ID is never visible
 * in the stored key name (e.g. `cap_sec_3_auth_refresh_token`).
 *
 * @param entityId - Process server entity ID from the JWT (`claim.entity_id`).
 */
function scopedAuthKeys(entityId: number) {
  const pfx = storagePrefix(entityId);
  return {
    REFRESH_TOKEN: `${pfx}_auth_refresh_token`,
    EXPIRES_AT:    `${pfx}_auth_expires_at`,
    USERNAME:      `${pfx}_auth_username`,
  } as const;
}

/**
 * Manages JWT-based authentication for the Titanium Legal process server app.
 *
 * @remarks
 * Uses Angular Signals for reactive auth state. The current token is persisted
 * in secure storage (Keychain on iOS, Keystore on Android) so sessions survive app restarts.
 *
 * **Storage key policy:**
 * - `auth_access_token` — stored **without** server ID (bootstrap key, read first on startup).
 * - `{hashedEntityId}_auth_refresh_token`, `_auth_expires_at`, `_auth_username` — scoped.
 *
 * **Login flow** — `POST /auth/token`
 * - Credentials are sent as `Authorization: Basic <base64>` (HTTP Basic Auth).
 * - The form body carries only `role=ps`.
 * - Five device-context headers are required per the API contract.
 *
 * **Refresh flow** — `POST /auth/refresh`
 * - The current `accessToken` is sent as a Bearer token.
 * - No separate refresh-token field exists in the API.
 *
 * **Logout flow** — `POST /auth/logout`
 * - Invalidates the token server-side then clears local state.
 *
 * @example
 * ```ts
 * const auth = inject(AuthService);
 * if (auth.isAuthenticated()) { ... }
 * await auth.login('user@example.com', 'secret');
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _token        = signal<AuthToken | null>(null);
  private readonly http          = inject(HttpClient);
  private readonly sentryService = inject(SentryService);
  private readonly idb           = inject(IdbStorageService);

  /**
   * Read-only signal containing the current {@link AuthToken}, or `null`
   * when the user is not authenticated.
   */
  readonly token = this._token.asReadonly();

  private readonly _sessionExpired$ = new Subject<void>();

  /**
   * Emits once whenever the session is forcibly terminated by
   * {@link clearSession} — i.e., when the refresh token is also expired.
   * `LoginComponent` subscribes to this to show the "Session expired" toast
   * after the login screen has finished rendering.
   */
  readonly sessionExpired$: Observable<void> = this._sessionExpired$.asObservable();

  private readonly _manualLogoutInProgress = signal(false);

  /**
   * `true` from the moment {@link logout} is called until the next
   * successful {@link login}.
   *
   * @remarks
   * **2026-07-29 fix** — lets `authInterceptor` distinguish a genuine
   * involuntary session expiration from a stray 401 on a request that
   * happened to be in flight when the user deliberately logged out.
   * `logout()`'s own doc comment already notes the server-side `POST
   * /auth/logout` call invalidates the token remotely — so any *other*
   * request already in flight at that moment (a background `autoSync()`
   * tick, a queued `EventLogService` flush, etc.) using the same
   * now-invalidated Bearer token can come back with a 401 shortly after.
   * `authInterceptor`'s `refreshAndRetry()` previously had no way to tell
   * that apart from a real expired-session 401 — its `!auth.token()` check
   * is `true` immediately after `logout()` clears the token either way — so
   * it called `clearSession()` (emitting {@link sessionExpired$}) on top of
   * an already-in-progress, perfectly normal logout. The officer saw
   * "Session expired. Please sign in again." immediately after tapping
   * "Log out" themselves. Reset to `false` on the next successful `login()`
   * so a real subsequent session-expiry event is still handled normally.
   */
  readonly manualLogoutInProgress = this._manualLogoutInProgress.asReadonly();

  /**
   * Computed signal that is `true` when a non-expired token is present.
   *
   * @remarks
   * Compares `Date.now()` against {@link AuthToken.expiresAt} so the value
   * becomes `false` automatically once the token window closes.
   */
  readonly isAuthenticated = computed(() => {
    const t = this._token();
    return t !== null && Date.now() < t.expiresAt;
  });

  /**
   * Restores the persisted token and returns when complete.
   * Called by the `APP_INITIALIZER` so that the auth guard can see the
   * correct `isAuthenticated()` state before the first route is resolved.
   *
   * @remarks
   * Replacing the fire-and-forget constructor call eliminates the race
   * condition where the guard runs before the async token restoration
   * finishes, causing an incorrect redirect to `/auth`.
   */
  async initialize(): Promise<void> {
    await this.restoreStoredToken();
  }

  /**
   * Resolves once the startup token-restore sequence has finished.
   *
   * @remarks
   * `authGuard` awaits this before checking {@link isAuthenticated} so the
   * guard never fires before the stored session has been read from secure
   * storage — preventing the spurious logout-on-reload race condition.
   *
   * Awaiting an already-resolved Promise is instantaneous, so subsequent
   * guard checks after the first navigation have zero overhead.
   */
  readonly initialized: Promise<void>;

  constructor() {
    this.initialized = this.restoreStoredToken();
  }

  /**
   * Authenticates the process server and stores the returned JWT.
   *
   * @remarks
   * Sends credentials as `Authorization: Basic <base64(username:password)>`
   * with form body `role=ps` to `POST /auth/token`.
   * Device-context headers are collected from Capacitor APIs before the request.
   * On success the token is persisted in secure storage and the
   * {@link token} signal is updated.
   *
   * @param username - Process server login username.
   * @param password - Login password (discarded after the response is received).
   * @throws When the API returns a non-2xx status or the network is unavailable.
   */
  async login(username: string, password: string): Promise<void> {
    const headers = await this.buildLoginHeaders(username, password);
    const body = new HttpParams().set('role', 'ps');

    const response = await firstValueFrom(
      this.http.post<AuthenticationResponse>(`${AUTH_BASE}/token`, body.toString(), { headers }),
    );

    const token = authTokenToModel(response);
    await this.persistToken(token);
    this._token.set(token);
    this._manualLogoutInProgress.set(false);
    // Immediately exchange for a long-lived token — authInterceptor attaches
    // the Bearer header automatically for this follow-up call.
    await this.refresh();
  }

  /**
   * Exchanges the current access token for a fresh one via
   * `POST /auth/refresh`.
   *
   * @remarks
   * The `authInterceptor` attaches `Authorization: Bearer` automatically.
   * Called automatically at the end of {@link login} and can be called
   * manually to extend the session before the token expires.
   * Updates {@link token} and secure storage on success.
   *
   * @throws When there is no current token, or the API returns a non-2xx status.
   */
  async refresh(): Promise<void> {
    const current = this._token();
    if (!current) throw new Error('AuthService.refresh: no active token to refresh.');

    const response = await firstValueFrom(
      this.http.post<AuthenticationResponse>(`${AUTH_BASE}/refresh`, null),
    );

    const token = authTokenToModel(response);
    await this.persistToken(token);
    this._token.set(token);
    this.sentryService.setUser({ id: String(token.user.entityId), email: token.user.email, username: token.user.name });
  }

  /**
   * Signs the user out and clears all local auth state.
   *
   * @remarks
   * Per spec §7.6 — "fire-and-forget: do not block on response".
   * The HTTP call is intentionally NOT awaited: we fire it and immediately
   * clear local state so the user is signed out without waiting for the server.
   * Local state is cleared even if the API call later fails.
   * The `authInterceptor` attaches `Authorization: Bearer` automatically.
   */
  async logout(): Promise<void> {
    this._manualLogoutInProgress.set(true);
    const current = this._token();

    // Fire-and-forget: do not await or block on the server response.
    // Token is still set in the signal when subscribe() is called so the
    // interceptor can read it before _token.set(null) clears it.
    if (current) {
      this.http
        .post<LogoutResponse>(`${AUTH_BASE}/logout`, null)
        .subscribe({ error: () => void 0 });
    }

    await this.clearAllStorage();
    this._token.set(null);
    this.sentryService.setUser(null);
  }

  /**
   * Clears local auth state without calling the logout API.
   *
   * @remarks
   * Used by {@link authInterceptor} when a session has definitively expired
   * (i.e., after `MAX_REFRESH_ATTEMPTS` consecutive 401 failures). Unlike
   * {@link logout}, this method does not fire a server-side logout request —
   * the token is already invalid so there is nothing to invalidate remotely.
   */
  async clearSession(): Promise<void> {
    await this.clearAllStorage();
    this._token.set(null);
    this.sentryService.setUser(null);
    this._sessionExpired$.next();
  }

  /**
   * Registers a new process-server account via `POST /auth/register`.
   *
   * @remarks
   * Sends the form data as `multipart/form-data` (required by the API).
   * On success the API returns a confirmation message; the user must then
   * verify their email before they can log in.
   *
   * @param data - Registration form values in camelCase domain shape.
   * @returns The server confirmation message to display to the user.
   */
  async register(data: PsRegistrationData): Promise<string> {
    const payload = registerToPayload(data);
    const formData = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      if (value !== undefined) {
        formData.append(key, String(value));
      }
    }
    const raw = await firstValueFrom(
      this.http.post<unknown>(`${AUTH_BASE}/register`, formData),
    );
    return registerUserToModel(raw).message;
  }

  /**
   * Requests a password-reset link from `GET /auth/forgetpassword`.
   *
   * @remarks
   * The API always responds with a success envelope regardless of whether an
   * account exists, preventing user enumeration.
   *
   * @param email - The registered email address of the process server.
   * @returns The server response message to display to the user.
   */
  async forgotPassword(email: string): Promise<string> {
    const raw = await firstValueFrom(
      this.http.get<unknown>(
        `${AUTH_BASE}/forgetpassword`,
        { params: new HttpParams().set('email', email) },
      ),
    );
    return forgetPasswordToModel(raw).message;
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  /**
   * Restores a persisted auth session from secure storage on app start.
   *
   * @remarks
   * Bootstrap sequence:
   * 1. Read the unscoped `auth_access_token` key.
   * 2. Decode the JWT to obtain the server entity ID.
   * 3. Read the scoped `{prefix}_auth_expires_at` key using that entity ID.
   * 4. Restore the signal or attempt a silent refresh if expired.
   *
   * Three startup scenarios per the auth spec (§7.7):
   * - **Valid token**: restore signal directly — no network call.
   * - **Expired token + refresh key present**: attempt a silent `POST /auth/refresh`.
   * - **No token / missing keys**: return — `authGuard` handles redirect.
   *
   * Also runs one-time migration to remove old unscoped keys written by
   * previous builds before server-ID scoping was introduced.
   */
  private async restoreStoredToken(): Promise<void> {
    // Migration: remove legacy unscoped keys from builds prior to server-ID scoping.
    await Promise.allSettled([
      'auth_token',
      'auth_refresh_token',
      'auth_expires_at',
      'auth_username',
    ].map(key => SecureStoragePlugin.remove({ key })));

    let accessToken: string;
    try {
      accessToken = (await SecureStoragePlugin.get({ key: ACCESS_TOKEN_KEY })).value;
    } catch {
      return;
    }

    const user = decodeUserFromJwt(accessToken);
    if (!user?.entityId) return;

    const scoped = scopedAuthKeys(user.entityId);
    let expiresAt: number;
    try {
      expiresAt = parseInt((await SecureStoragePlugin.get({ key: scoped.EXPIRES_AT })).value, 10);
    } catch {
      return;
    }

    const token = this.buildRestoredToken(accessToken, expiresAt);

    if (Date.now() < expiresAt) {
      this._token.set(token);
      this.sentryService.setUser({ id: String(token.user.entityId), email: token.user.email, username: token.user.name });
      return;
    }

    await this.attemptStartupRefresh(token);
  }

  /**
   * Constructs an {@link AuthToken} from individual stored fields.
   * Called during restore when a full API response is not available.
   *
   * @param accessToken - Stored JWT string.
   * @param expiresAt   - Stored expiry timestamp in milliseconds.
   */
  private buildRestoredToken(accessToken: string, expiresAt: number): AuthToken {
    return {
      accessToken,
      tokenType: 'bearer',
      expiresIn: Math.max(0, Math.round((expiresAt - Date.now()) / 1000)),
      expiresAt,
      user: decodeUserFromJwt(accessToken),
    };
  }

  /**
   * Attempts a silent token refresh during startup when the stored token is expired.
   * Sets the expired token on the signal temporarily so the interceptor can
   * attach a Bearer header to the refresh request.
   *
   * @param expiredToken - Reconstructed (expired) token to use as the refresh credential.
   */
  private async attemptStartupRefresh(expiredToken: AuthToken): Promise<void> {
    const scoped = scopedAuthKeys(expiredToken.user.entityId);
    try {
      await SecureStoragePlugin.get({ key: scoped.REFRESH_TOKEN });
    } catch {
      return;
    }
    this._token.set(expiredToken);
    try {
      await this.refresh();
    } catch {
      await this.clearAllStorage();
      this._token.set(null);
    }
  }

  /**
   * Persists all auth keys to secure storage.
   *
   * `auth_access_token` is stored without a server ID (bootstrap key).
   * The remaining three keys are scoped to `{hashedEntityId}_*` so different
   * users on the same device never share or expose each other's session data.
   *
   * @param token - The token to persist.
   */
  private async persistToken(token: AuthToken): Promise<void> {
    const scoped = scopedAuthKeys(token.user.entityId);
    await SecureStoragePlugin.set({ key: ACCESS_TOKEN_KEY,      value: token.accessToken });
    await SecureStoragePlugin.set({ key: scoped.REFRESH_TOKEN,  value: token.accessToken });
    await SecureStoragePlugin.set({ key: scoped.EXPIRES_AT,     value: String(token.expiresAt) });
    await SecureStoragePlugin.set({ key: scoped.USERNAME,       value: token.user.name });
  }

  /**
   * Clears all user data from storage on logout (SecureStoragePlugin + localStorage).
   *
   * @remarks
   * Removes all auth tokens and app caches to ensure a completely clean state for
   * the next user login. This is simpler than tracking individual keys and ensures
   * nothing is left behind from the previous session.
   *
   * **Cleared from SecureStoragePlugin:**
   * - All auth tokens and scoped app data
   *
   * **Cleared from localStorage:**
   * - All user data (legacy pending photos, unscoped queue items, etc.)
   *
   * **Cleared from IndexedDB (2026-08-05):**
   * - Every store in `IDB_STORES_CLEARED_ON_LOGOUT` — the offline submission
   *   queue, pending
   *   evidence photos, which moved out of localStorage to escape its ~5 MB
   *   quota. The localStorage sweep below does NOT reach them, so forgetting
   *   this would leave one officer's queued submissions and photos readable by
   *   the next user on a shared device.
   *
   * **`{pfx}_queue_submissions` (the offline upqueue) IS included in this wipe
   * — confirmed as the intended behavior (2026-07-29):** a fix was briefly
   * attempted to exempt this key, reasoning from `UpqueueService`'s own doc
   * comment ("the queue survives logout... so unsent submissions are
   * restored when the same user logs back in"). That was reverted per
   * explicit product direction: "Log out anyway" — the only path through
   * which a logout can happen while the queue is non-empty (`onAvatarTap()`
   * only ever shows the plain, no-pending-items logout sheet when
   * `upqueueBadge() === 0`; the warning sheet with "Log out anyway" is the
   * sole entry point otherwise) — is a deliberate, warned choice ("N unsent
   * submissions — Upload them before logging out to avoid losing field
   * data... Log out anyway"), and once made, storage really should be fully
   * cleared, not partially preserved. `UpqueueService`'s own "queue survives
   * logout" doc comment is now stale relative to this decision and should be
   * corrected there rather than re-read as a reason to exempt this key
   * again — the queue's *in-memory* signal already clears on logout
   * regardless; this method additionally clears its *persisted* copy too.
   */
  private async clearAllStorage(): Promise<void> {
    // Clear all SecureStoragePlugin entries (all start with cap_sec_)
    const allKeys = Object.keys(localStorage);
    const userKeys = allKeys.filter(key =>
      // Keep only non-user-specific keys (app settings is shared across users)
      !key.includes('app_settings_cache')
    );

    // Clear SecureStoragePlugin by removing all user-scoped keys
    // Since we can't enumerate SecureStoragePlugin directly, we remove known patterns
    const entityId = this._token()?.user?.entityId;
    if (entityId) {
      const pfx = storagePrefix(entityId);
      const scoped = scopedAuthKeys(entityId);
      await Promise.allSettled([
        SecureStoragePlugin.remove({ key: ACCESS_TOKEN_KEY }),
        SecureStoragePlugin.remove({ key: scoped.REFRESH_TOKEN }),
        SecureStoragePlugin.remove({ key: scoped.EXPIRES_AT }),
        SecureStoragePlugin.remove({ key: scoped.USERNAME }),
        SecureStoragePlugin.remove({ key: `${pfx}_cache_jobs` }),
        SecureStoragePlugin.remove({ key: `${pfx}_cache_jobs_at` }),
        SecureStoragePlugin.remove({ key: `${pfx}_queue_submissions` }),
        SecureStoragePlugin.remove({ key: `${pfx}_pending_event_logs` }),
        SecureStoragePlugin.remove({ key: `${pfx}_ref_data_cache` }),
        SecureStoragePlugin.remove({ key: `${pfx}_esign_data` }),
      ]);
    }

    // Clear all localStorage entries except app settings (shared across users)
    for (const key of userKeys) {
      try { localStorage.removeItem(key); } catch { /* unavailable */ }
    }

    // Clear every IndexedDB store (2026-08-05) — the offline queue and pending
    // evidence photos moved there from localStorage, so the sweep above no
    // longer reaches them. Whole stores rather than individual keys: both are
    // keyed per user/job, and a logout should leave nothing behind for the next
    // officer on a shared device. See IdbStorageService for the move's rationale.
    await Promise.allSettled(IDB_STORES_CLEARED_ON_LOGOUT.map(store => this.idb.clearStore(store)));
  }

  /**
   * Builds the `HttpHeaders` required by `POST /auth/token`.
   *
   * @param username - Username for the Basic Auth header.
   * @param password - Password for the Basic Auth header.
   * @returns Headers including Basic Auth, Content-Type, and all device fields.
   */
  private async buildLoginHeaders(username: string, password: string): Promise<HttpHeaders> {
    const [deviceId, deviceType, appVersion, coords] = await Promise.all([
      this.getDeviceId(),
      this.getDeviceType(),
      this.getAppVersion(),
      this.getCoordinates(),
    ]);

    return new HttpHeaders({
      Authorization: `Basic ${btoa(`${username}:${password}`)}`,
      'Content-Type': 'application/x-www-form-urlencoded',
      'Device-ID': deviceId,
      'Device-Type': deviceType,
      'Device-Latitude': coords.latitude,
      'Device-Longitude': coords.longitude,
      'App-Version': appVersion,
    });
  }

  /**
   * Returns the unique device identifier, falling back to a random UUID
   * on platforms where the plugin is unavailable.
   */
  private async getDeviceId(): Promise<string> {
    try {
      const info = await Device.getId();
      return info.identifier;
    } catch {
      return crypto.randomUUID();
    }
  }

  /**
   * Returns the platform string expected by the API (`"iOS"`, `"Android"`,
   * or `"Web"`).
   */
  private async getDeviceType(): Promise<string> {
    try {
      const info = await Device.getInfo();
      const map: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web' };
      return map[info.platform] ?? 'Web';
    } catch {
      return 'Web';
    }
  }

  /**
   * Returns the installed app version string, falling back to `"1.0.0"`.
   */
  /**
   * Returns the app version string from Capacitor or package.json.
   * Formats: `{versionName}-{versionCode}` on native, `{version}` on web.
   */
  private async getAppVersion(): Promise<string> {
    try {
      const info = await App.getInfo();
      return `${info.version}-${info.build}`;
    } catch {
      // Web/browser fallback to package.json version
      return '1.0.0';
    }
  }

  /**
   * Returns the device's current latitude and longitude as strings,
   * falling back to `"0"` / `"0"` when Geolocation permission is denied,
   * the plugin is unavailable, or the iOS CLLocationManager RunLoop hang
   * causes the native `timeout` option to silently fail to fire.
   *
   * `Promise.race` provides a JS-level safety net at {@link GEO_JS_TIMEOUT_MS}
   * so the login request is never blocked indefinitely on a hung native call.
   */
  private async getCoordinates(): Promise<{ latitude: string; longitude: string }> {
    try {
      const position = await Promise.race([
        Geolocation.getCurrentPosition({ timeout: 5000 }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('geolocation timeout')), GEO_JS_TIMEOUT_MS),
        ),
      ]);
      return {
        latitude: position.coords.latitude.toFixed(6),
        longitude: position.coords.longitude.toFixed(6),
      };
    } catch {
      return { latitude: '0', longitude: '0' };
    }
  }
}
